/**
 * Historical cut-off banner shown above every MSR3 royalties region page.
 *
 * Copy is preferably driven by FE Parameter Store via
 * `GET /setting/configuration` (SSM path
 * `/screenrights.app/front-end/<env>/configuration/<key>`), updated with
 * `PUT /setting/configuration` (`EditSettings`) — no FE deploy required.
 *
 * **Each region owns its own keys.** Member Services words the ANZ banner for
 * Australia and NZ (ANZ payment cycles, ANZ cut-off dates); World must
 * not inherit that verbatim, and giving World its own wording must not
 * need a frontend deploy. The fallback below is deliberately region-neutral so
 * both pages read correctly before any key is set.
 */
import { readConfigString } from '../utils/helpers/configurationValue';

export type RoyaltiesHistoricalBanner = {
  body: string;
  title: string;
};

/** Parameter Store keys for one region's banner. */
export type RoyaltiesHistoricalBannerConfigKeys = {
  historicalBannerBody: string;
  historicalBannerTitle: string;
};

/** Region-neutral fallback, used until a region's keys are set / approved. */
export const ROYALTIES_HISTORICAL_BANNER_FALLBACK = {
  body: 'This page shows payments through the new royalty system. For earlier payments, see Historic Royalties under Earnings Statements.',
  title: 'About these statements',
} as const satisfies RoyaltiesHistoricalBanner;

/** Royalties ANZ banner keys. */
export const ROYALTIES_ANZ_HISTORICAL_BANNER_KEYS = {
  historicalBannerBody: 'royaltiesAnz.historicalBanner.body',
  historicalBannerTitle: 'royaltiesAnz.historicalBanner.title',
} as const satisfies RoyaltiesHistoricalBannerConfigKeys;

/** Royalties World banner keys — independent of ANZ. */
export const ROYALTIES_WORLD_HISTORICAL_BANNER_KEYS = {
  historicalBannerBody: 'royaltiesWorld.historicalBanner.body',
  historicalBannerTitle: 'royaltiesWorld.historicalBanner.title',
} as const satisfies RoyaltiesHistoricalBannerConfigKeys;

/**
 * Resolve one region's banner copy from shared FE configuration, falling back
 * to {@link ROYALTIES_HISTORICAL_BANNER_FALLBACK} per field when a key is
 * missing, empty, or not a string.
 */
export const resolveRoyaltiesHistoricalBanner = (
  configuration: Record<string, unknown> | null | undefined,
  keys: RoyaltiesHistoricalBannerConfigKeys,
): RoyaltiesHistoricalBanner => ({
  body:
    readConfigString(configuration, keys.historicalBannerBody) ||
    ROYALTIES_HISTORICAL_BANNER_FALLBACK.body,
  title:
    readConfigString(configuration, keys.historicalBannerTitle) ||
    ROYALTIES_HISTORICAL_BANNER_FALLBACK.title,
});
